/* eslint-disable @typescript-eslint/no-explicit-any */
import type { TypedDocumentNode } from "@graphql-typed-document-node/core";
import type { VariablesOf, ResultOf } from "gql.tada";
import type { FetchError } from "./types";
import { REQUEST_TIMEOUT } from "./constants";
import { gqlFetch } from "./gqlFetch";
import { getUserAction } from "stores/user";

export const gqlFetchWithAuth = async <
  Query extends TypedDocumentNode<any, any>,
>({
  query,
  variables,
  signal,
  timeout = REQUEST_TIMEOUT,
  disableRefresh = false,
}: {
  query: Query;
  variables?: VariablesOf<Query>;
  signal?: AbortSignal;
  timeout?: number;
  disableRefresh?: boolean;
}): Promise<ResultOf<Query>> => {
  const args = { query, variables, signal, timeout };
  try {
    return await gqlFetch(args);
  } catch (err) {
    // gqlFetch always throws FetchError due to mapError
    const fetchError = err as FetchError;
    // Only retry on 403 GraphQL errors (auth refresh needed)
    if (fetchError.type !== "not_authenticated") {
      throw fetchError;
    }
    if (disableRefresh) {
      console.warn(
        "[gqlFetchWithAuth] Received 403/401 error but auth refresh is disabled. Propagating error.",
      );
      throw fetchError;
    }
    // Attempt token refresh
    const refreshed = await getUserAction().refresh();
    if (refreshed !== true) {
      throw new Error("Token refresh failed");
    }
    try {
      return await gqlFetch(args); // replay once with fresh token
    } catch (retryErr) {
      // If retry also fails with 403, the session is genuinely expired/invalid
      // Log for debugging but let it propagate as an unhandled rejection is expected here
      const retryError = retryErr as FetchError;
      if (retryError.type === "not_authenticated") {
        console.warn(
          "[gqlFetchWithAuth] Retry after refresh still failed with 403/401 - user session is no longer valid",
        );
      }
      throw retryErr;
    }
  }
};
